import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function SharePromptModal({ prompt, onClose }) {
  const [connections, setConnections] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/connections`, {
        withCredentials: true,
        headers
      });
      setConnections(data);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const handleShare = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one connection');
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/share`,
        { prompt_id: prompt.prompt_id, user_ids: selected },
        { withCredentials: true, headers }
      );
      toast.success('Prompt shared successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to share prompt');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (userId) => {
    if (selected.includes(userId)) {
      setSelected(selected.filter(id => id !== userId));
    } else {
      setSelected([...selected, userId]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-[360px] shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl tracking-tight font-semibold text-[#1C1917]">Share Prompt</h2>
          <button onClick={onClose} className="text-[#78716C] hover:text-[#1C1917]">
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-[#57534E] mb-4">Select connections to share "{prompt.title}" with:</p>

        <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
          {connections.length === 0 ? (
            <p className="text-sm text-[#78716C] text-center py-4">No connections yet</p>
          ) : (
            connections.map((conn) => {
              const otherUser = conn.user_details;
              if (!otherUser) return null;

              return (
                <label
                  key={conn.connection_id}
                  className="flex items-center gap-3 p-3 border border-[#E7E5E4] rounded-lg hover:bg-[#FAFAF9] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(otherUser.user_id)}
                    onChange={() => toggleSelection(otherUser.user_id)}
                    className="rounded border-[#E7E5E4] text-[#2C4C3B] focus:ring-[#2C4C3B]"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1C1917]">{otherUser.name}</p>
                    <p className="text-xs text-[#78716C]">{otherUser.email}</p>
                  </div>
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#F5F2EA] text-[#78716C]">
                    {conn.connection_type}
                  </span>
                </label>
              );
            })
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={loading || selected.length === 0}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-[#E7E5E4] focus:outline-none bg-[#2C4C3B] text-white hover:bg-[#243D30] flex-1 disabled:opacity-50"
          >
            {loading ? 'Sharing...' : `Share (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}